import { DatePicker } from "@app/components/DatePicker.tsx";
import Kvp from "@app/components/kvp.tsx";
import { ActiveEnums } from "@app/data/engine.ts";
import useSetupCityStateData from "@app/data-hooks/setup/use-setup-city-state-data.ts";
import useSetupExtensionsData from "@app/data-hooks/setup/use-setup-extensions-data.ts";
import useSetupStateCodeData from "@app/data-hooks/setup/use-setup-state-code-data.ts";
import useSetupUtilizationCodesData from "@app/data-hooks/setup/use-setup-utilization-codes-data.ts";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import { formatDate } from "@app/helpers/time-helper.ts";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid2,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { QueryStatusRouter, Select } from "bi-lib";
import moment from "moment";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface Props {
    id?: number;
    clientId?: number;
}

export default function AssignmentGeneralForm({ id, clientId }: Props) {
    const { completeAssignment, updateGeneral, insertAssignment } = useAssignmentData({ id });
    const { stateCodes } = useSetupStateCodeData();
    const { cityStates } = useSetupCityStateData();
    const { extensions } = useSetupExtensionsData();
    const { utilizationCodes } = useSetupUtilizationCodesData();
    const { rosterList } = useAssignmentData({ id, activeState: ActiveEnums.All });

    const navigate = useNavigate();

    const [stateId, setStateId] = useState<
        { label: string | undefined | null; value: number | undefined } | undefined
    >();
    const [cityName, setCityName] = useState<string | undefined>();

    useEffect(() => {
        if (completeAssignment.data == undefined) {
            return;
        }
        setStateId({ label: completeAssignment.data.stateName, value: completeAssignment.data.stateCodeId });
        setCityName(completeAssignment.data.city ?? undefined);
    }, [completeAssignment.data]);

    const form = useForm({
        defaultValues: {
            startDate: completeAssignment.data?.startDate,
            durationMonths: completeAssignment.data?.durationMonths,
            assignDesc: completeAssignment.data?.assignDesc,
            inRosterToEnter: completeAssignment.data?.inRosterToEnter,
            workPackageCodeCharge: completeAssignment.data?.workPackageCodeCharge,
            workLocationCharge: completeAssignment.data?.workLocationCharge,
            taskCodeCharge: completeAssignment.data?.taskCodeCharge,
            budgetYearRequired: completeAssignment.data?.budgetYearRequired,
            isActive: id == null ? true : completeAssignment.data?.isActive,
            utilizationCodeId: completeAssignment.data?.utilizationCodeId,
            projectManagerId: completeAssignment.data?.projectManagerId,
            extensionId: completeAssignment.data?.extensionId,
        },
        onSubmit: async ({ value }) => {
            const cityStateId = cityStates.data?.find(
                (x) => x.stateCodeId == stateId?.value && x.city == cityName,
            )?.cityStateId;
            if (id != undefined) {
                void updateGeneral.mutate({
                    assignmentId: id,
                    startDate: value.startDate,
                    duration: value.durationMonths?.toString(),
                    description: value.assignDesc,
                    inRoster: value.inRosterToEnter,
                    workPackageRequired: value.workPackageCodeCharge,
                    workLocationRequired: value.workLocationCharge,
                    taskCodeRequired: value.taskCodeCharge,
                    yearRequired: value.budgetYearRequired,
                    isActive: value.isActive,
                    utilizationCodeId: value.utilizationCodeId?.toString(),
                    projectManagerId: value.projectManagerId?.toString(),
                    defaultWorkLocationStateId: stateId?.value?.toString(),
                    defaultWorkLocationCityName: cityStateId == undefined ? cityName : undefined,
                    workLocationId: cityStateId?.toString(),
                });
                return;
            }

            if (clientId != undefined) {
                const response = await insertAssignment.mutateAsync({
                    clientId: clientId.toString(),
                    startDate: value.startDate,
                    duration: value.durationMonths?.toString(),
                    description: value.assignDesc,
                    inRoster: value.inRosterToEnter,
                    workPackageRequired: value.workPackageCodeCharge,
                    workLocationRequired: value.workLocationCharge,
                    taskCodeRequired: value.taskCodeCharge,
                    yearRequired: value.budgetYearRequired,
                    isActive: value.isActive,
                    utilizationCodeId: value.utilizationCodeId?.toString(),
                    projectManagerId: value.projectManagerId?.toString(),
                    defaultWorkLocationStateId: stateId?.value?.toString(),
                    defaultWorkLocationCityName: cityStateId == undefined ? cityName : undefined,
                    workLocationId: cityStateId?.toString(),
                    extensionId: value.extensionId?.toString(),
                });
                void navigate({
                    to: "/assignment/$assignmentId",
                    params: { assignmentId: response.assignmentId?.toString() ?? "" },
                });
                return;
            }

            toast.error("No client id or assignment id provided");
        },
    });

    return (
        <QueryStatusRouter queryHook={completeAssignment}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <Stack spacing={2}>
                    <Paper sx={{ padding: 2 }}>
                        <Typography variant={"h5"}>General</Typography>
                        <Grid2 container spacing={2} mt={1}>
                            <Grid2 size={6}>
                                <Stack spacing={1}>
                                    <QueryStatusRouter queryHook={utilizationCodes}>
                                        <form.Field name={"utilizationCodeId"}>
                                            {(field) => (
                                                <Select
                                                    label={"Utilization Code"}
                                                    value={field.state.value}
                                                    onChange={(e) => field.handleChange(e)}
                                                    fullWidth
                                                    required
                                                    options={utilizationCodes.data?.map((x) => ({
                                                        label: x.displayName,
                                                        value: x.utilizationCodeId,
                                                    }))}
                                                />
                                            )}
                                        </form.Field>
                                    </QueryStatusRouter>
                                    <QueryStatusRouter queryHook={extensions}>
                                        <form.Field name={"extensionId"}>
                                            {(field) => (
                                                <Select
                                                    label={"Extension"}
                                                    value={field.state.value}
                                                    onChange={(e) => field.handleChange(e)}
                                                    fullWidth
                                                    disabled={id != undefined}
                                                    required
                                                    options={extensions.data?.map((x) => ({
                                                        label: x.displayName,
                                                        value: x.extensionId,
                                                    }))}
                                                />
                                            )}
                                        </form.Field>
                                    </QueryStatusRouter>
                                    <Kvp keyTitle={"Assignment Code"}>{completeAssignment.data?.assignNum}</Kvp>
                                    <form.Field
                                        name={"startDate"}
                                        validators={{
                                            onChange: ({ value }) => {
                                                if (value == undefined) {
                                                    return "Required";
                                                }
                                                const minDate = moment("1753-01-01T00:00:00.000Z");
                                                if (moment(value) < minDate) {
                                                    return `Date cannot be before ${formatDate(minDate)}`;
                                                }
                                            },
                                        }}
                                    >
                                        {(field) => (
                                            <>
                                                <DatePicker
                                                    value={field.state.value ? moment(field.state.value) : null}
                                                    label={"Start Dates"}
                                                    onChange={(e) => field.handleChange(e?.format("MM/DD/YYYY"))}
                                                    minDate={moment("1753-01-01T00:00:00.000Z")}
                                                />
                                                {field.state.meta.errors.length > 0 &&
                                                    field.state.meta.errors.map((x, i) => (
                                                        <Typography key={i} variant={"body2"} color={"error"}>
                                                            {x}
                                                        </Typography>
                                                    ))}
                                            </>
                                        )}
                                    </form.Field>
                                    <form.Field name={"durationMonths"}>
                                        {(field) => (
                                            <TextField
                                                type={"number"}
                                                value={field.state.value ?? ""}
                                                label={"Duration (in months)"}
                                                onChange={(e) => field.handleChange(+e.target.value)}
                                            />
                                        )}
                                    </form.Field>
                                </Stack>
                            </Grid2>
                            <Grid2 size={6}>
                                <Stack spacing={1}>
                                    <form.Field name={"projectManagerId"}>
                                        {(field) => (
                                            <Select
                                                label={"Project Manager"}
                                                value={field.state.value}
                                                onChange={(e) => field.handleChange(e)}
                                                options={rosterList.data?.map((x) => ({
                                                    label: `${x.lastName}, ${x.firstName}`,
                                                    value: x.staffId,
                                                }))}
                                                required
                                            />
                                        )}
                                    </form.Field>
                                    <QueryStatusRouter queryHook={stateCodes}>
                                        <Autocomplete
                                            renderInput={(params) => (
                                                <TextField {...params} label={"Default State"} required />
                                            )}
                                            options={
                                                stateCodes.data?.map((x) => ({
                                                    label: x.stateName,
                                                    value: x.stateCodeId,
                                                })) ?? []
                                            }
                                            value={stateId ?? null}
                                            onChange={(_e, value) => {
                                                setStateId(value ?? undefined);
                                                setCityName(undefined);
                                            }}
                                        />
                                    </QueryStatusRouter>
                                    <QueryStatusRouter queryHook={cityStates}>
                                        <Autocomplete
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={"Default City"}
                                                    onChange={(e) => setCityName(e.target.value)}
                                                    required
                                                />
                                            )}
                                            options={
                                                cityStates.data
                                                    ?.filter((x) => x.stateCodeId == stateId?.value)
                                                    .map((x) => ({
                                                        label: x.city,
                                                        value: x.cityStateId,
                                                    })) ?? []
                                            }
                                            value={cityName ?? ""}
                                            onChange={(_e, value) => {
                                                let processedValue;
                                                if (value == undefined || typeof value == "string") {
                                                    processedValue = value;
                                                } else {
                                                    processedValue = value.label;
                                                }
                                                setCityName(processedValue ?? undefined);
                                            }}
                                            freeSolo={true}
                                        />
                                    </QueryStatusRouter>
                                    <form.Field name={"assignDesc"}>
                                        {(field) => (
                                            <TextField
                                                value={field.state.value ?? ""}
                                                label={"Description"}
                                                onChange={(e) => field.handleChange(e.target.value)}
                                                multiline
                                                rows={3}
                                                required
                                            />
                                        )}
                                    </form.Field>
                                    <form.Field name={"isActive"}>
                                        {(field) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={field.state.value ?? false}
                                                        onChange={(e) => field.handleChange(e.target.checked)}
                                                    />
                                                }
                                                label={"Active"}
                                            />
                                        )}
                                    </form.Field>
                                </Stack>
                            </Grid2>
                        </Grid2>
                    </Paper>
                    <Paper sx={{ padding: 2 }}>
                        <Typography variant={"h5"} mb={2}>
                            Behavior
                        </Typography>
                        <Stack spacing={1}>
                            <form.Field name={"inRosterToEnter"}>
                                {(field) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.state.value ?? false}
                                                onChange={(e) => field.handleChange(e.target.checked)}
                                            />
                                        }
                                        label={"Staff member must be on roster to bill"}
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"workPackageCodeCharge"}>
                                {(field) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.state.value ?? false}
                                                onChange={(e) => field.handleChange(e.target.checked)}
                                            />
                                        }
                                        label={"Work package required for time/expense entry"}
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"workLocationCharge"}>
                                {(field) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.state.value ?? false}
                                                onChange={(e) => field.handleChange(e.target.checked)}
                                            />
                                        }
                                        label={"Location required for time/expense entries"}
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"budgetYearRequired"}>
                                {(field) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.state.value ?? false}
                                                onChange={(e) => field.handleChange(e.target.checked)}
                                            />
                                        }
                                        label={"Year required for budget entries"}
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Paper>
                    <Box display={"flex"} justifyContent={"end"} gap={1}>
                        <Link to={"/assignment"}>
                            <Button variant={"outlined"}>Cancel</Button>
                        </Link>
                        <Button type={"submit"} variant={"contained"}>
                            Save
                        </Button>
                    </Box>
                </Stack>
            </form>
        </QueryStatusRouter>
    );
}
